import { Box, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import DateRangePicker, { getTimeframeDates } from "../../../App/Components/DateRange/DateRangePicker";
import { DisplayChangeDetails } from "../../../App/Components/MetricCard/MetricCard";
import { CampaignReportTimeframe } from "../../../App/Enums/CampaignTypes";
import { CampaignProfileCandidatesCapturedReport } from "../../../Reports/CandidatesAddedByCampaign/CampaignProfileCandidatesCapturedReport";
import { CampaignProfileState } from "../../State/CampaignProfileState";
import { useCampaignProfileReportContext } from "../../State/CampaignReportContext";

export const FormCapturesTimeframe = ({ state }: { state: CampaignProfileState }) => {
  const {
    getters: { startDate: customStartDate, endDate: customEndDate },
  } = state;

  const {
    startDate,
    endDate,
    handleSetReportEndDate,
    handleSetReportStartDate,
    timeframeOption,
    handleSetTimeframeOption,
    candidatesAddedState,
  } = useCampaignProfileReportContext();

  const {
    getters: {
      reportData,
      previousCount,
      percentChange,
      prevStartDate,
      prevEndDate,
      isLoadingPrevReport,
      startDate: reportStart,
      endDate: reportEnd,
    },
  } = candidatesAddedState;

  const totalCandidates =
    reportData?.campaignsInReport.reduce((acc, curr) => acc + curr.countInReportPeriod, 0) || 0;

  const { startDate: timeframeStart, endDate: timeframeEnd } = useMemo(
    () => getTimeframeDates(timeframeOption, reportStart, reportEnd),
    [timeframeOption, reportStart, reportEnd],
  );

  const timeframeDisplay = useMemo(
    () => `${timeframeStart.toLocaleDateString()} - ${timeframeEnd.toLocaleDateString()}`,
    [timeframeStart, timeframeEnd],
  );

  return (
    <>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        alignItems="center"
        paddingLeft={{ xs: 1, md: 2 }}
        paddingRight={{ xs: 1, md: 2 }}
        paddingTop="0"
        paddingBottom={1}
        mb={1}
        gap={{ xs: 1, md: 2, lg: 4 }}
      >
        <Box
          sx={{
            flex: "1 0 50%",
            maxWidth: {
              xs: "100%",
              lg: timeframeOption === CampaignReportTimeframe.Custom ? "50%" : "49.5ch",
            },
            display: "flex",
            alignItems: "center",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          <DateRangePicker
            id="campaign-report-dates"
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            startDate={startDate}
            endDate={endDate}
            handleSetStartDate={handleSetReportStartDate}
            handleSetEndDate={handleSetReportEndDate}
            customTimeframeOption={timeframeOption}
            customTimeframeLabel="Select a Timeframe"
            customHandleChangeTimeframe={handleSetTimeframeOption}
            dropdownSx={{
              mb: 0,
            }}
            dateRangeSx={{
              flexDirection: { xs: "column", sm: "row" },
            }}
          />
        </Box>
        <Box sx={{ width: "100%", minWidth: "19ch", flex: 0 }}>
          <Typography data-test="campaign-candidate-captures-timeframe" sx={{ mb: 1 }}>
            <strong>Candidate Captures</strong>
            <br />({timeframeDisplay})
          </Typography>
          <Typography
            data-test="campaign-candidate-captures"
            variant="h4"
            component="div"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {totalCandidates}
            <DisplayChangeDetails
              previousNumber={previousCount}
              percentChange={percentChange}
              prevStartDate={prevStartDate}
              prevEndDate={prevEndDate}
              isLoading={isLoadingPrevReport}
            />
          </Typography>
        </Box>
      </Stack>
      <CampaignProfileCandidatesCapturedReport />
    </>
  );
};
